import React from 'react';
import { FileText, Sparkles, Car, Package, ShoppingBag, ArrowUpRight, Plus } from 'lucide-react';
import { Button } from './ui/button';

const Products = () => {
  const services = [
    {
      title: 'RentEase',
      description: 'Digital Rental Agreements',
      icon: FileText,
      color: 'from-teal-500 to-cyan-500'
    },
    {
      title: 'Deep Cleaning',
      description: 'Professional Home Services',
      icon: Sparkles,
      color: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Car Wash',
      description: 'Waterless Car Cleaning',
      icon: Car,
      color: 'from-cyan-500 to-blue-500'
    },
    {
      title: 'Courier',
      description: 'Fast Delivery in Bangalore',
      icon: Package,
      color: 'from-violet-500 to-purple-500'
    },
    {
      title: 'Sell Anything',
      description: 'Buy & Sell Marketplace',
      icon: ShoppingBag,
      color: 'from-amber-500 to-orange-500'
    }
  ];

  return (
    <section id="products" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Products</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mt-3 mb-6">
            MANATRIBAN
          </h2>
          <p className="text-slate-600 text-lg max-w-3xl mx-auto">
            A comprehensive multi-service platform offering digital solutions for everyday needs. 
            From rental agreements to home services, we've got you covered.
          </p>
        </div>

        {/* Product Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 md:p-12 overflow-hidden relative">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            {/* Product Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 rounded-full mb-4">
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-pulse"></span>
                  <span className="text-teal-300 text-sm font-medium">Live Platform</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  All-in-One Service Platform
                </h3>
                <p className="text-slate-400 max-w-xl">
                  Simplifying everyday tasks with a suite of digital services designed for modern living in Bangalore and beyond.
                </p>
              </div>
              <a 
                href="https://manatriban.com" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-6 text-base font-medium transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/25 group">
                  Visit MANATRIBAN
                  <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>
              </a>
            </div>

            {/* Services Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700 hover:border-slate-600 rounded-xl p-4 text-center transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`w-12 h-12 mx-auto mb-3 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <service.icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-white font-semibold text-sm mb-1">{service.title}</h4>
                  <p className="text-slate-400 text-xs">{service.description}</p>
                </div>
              ))}
              
              {/* Coming Soon Card */}
              <div className="bg-slate-800/30 border border-dashed border-slate-600 rounded-xl p-4 text-center flex flex-col items-center justify-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-slate-700 rounded-xl flex items-center justify-center">
                  <Plus className="w-6 h-6 text-slate-400" />
                </div>
                <h4 className="text-slate-400 font-semibold text-sm mb-1">Coming Soon</h4>
                <p className="text-slate-500 text-xs">More services</p>
              </div>
            </div>

            {/* Features Pills */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              {['Digital Payments', 'Secure Platform', 'Local Services', '24/7 Support'].map((feature, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-full text-slate-300 text-sm"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Products;
